import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LuChartColumn, LuChevronRight } from 'react-icons/lu';
import { useReportStore } from '../../store/reports/reportStore';
import useSchoolStore from '../../store/schoolStore';
import PageHeader from '../../components/common/PageHeader';
import Card from '../../components/common/Card';
import Table, { tdClass } from '../../components/common/Table';
import Badge, { toneForStatus } from '../../components/common/Badge';
import EmptyState from '../../components/common/EmptyState';
import ErrorWidget from '../../components/common/ErrorWidget';
import LoadingMessage from '../../components/common/LoadingMessage';

/**
 * Class reports made for the school. A row opens its report items (ReportItems).
 * Data comes from useReportStore.getReports.
 */
export default function ClassReportsPage() {
    const { reports, isLoadingReports, error, getReports } = useReportStore();
    const { schoolId } = useSchoolStore();
    const navigate = useNavigate();

    useEffect(() => {
        if (schoolId) {
            getReports(schoolId);
        }
    }, [schoolId, getReports]);

    const reportItemsPath = (report) => `/dash/${schoolId}/reports/${report.id}/items`;

    const renderBody = () => {
        if (isLoadingReports) {
            return <LoadingMessage message="Loading class reports…" />;
        }

        if (error) {
            return (
                <ErrorWidget
                    error={{ message: error }}
                    title="Could not load the class reports"
                    onRetry={() => getReports(schoolId)}
                    className="my-8 border-0 shadow-none"
                />
            );
        }

        if (!reports || reports.length === 0) {
            return (
                <EmptyState
                    icon={<LuChartColumn />}
                    title="No class reports yet"
                    message="Class reports appear here once they are made, for example from Bulk reports."
                />
            );
        }

        // Batch reports have no class, section or status, so those columns appear only when some report has them
        const hasStatus = reports.some((report) => report.status);

        return (
            <Table head={hasStatus ? ['Report', 'Status', ''] : ['Report', '']}>
                {reports.map((report) => (
                    <tr key={report.id} onClick={() => navigate(reportItemsPath(report))} className="cursor-pointer">
                        <td className={tdClass}>
                            <p className="font-medium">{report.title || report.name || `Report ${report.id}`}</p>
                            {report.className && (
                                <p className="text-xs text-ui-ink-3">
                                    Class {report.className}{report.sec ? ` · Section ${report.sec}` : ''}
                                </p>
                            )}
                        </td>
                        {hasStatus && (
                            <td className={tdClass}>
                                {report.status ? <Badge tone={toneForStatus(report.status)}>{report.status}</Badge> : '—'}
                            </td>
                        )}
                        <td className={`${tdClass} w-24 text-right`}>
                            <Link
                                to={reportItemsPath(report)}
                                onClick={(event) => event.stopPropagation()}
                                className="inline-flex items-center gap-1 rounded text-[13px] font-medium text-ui-link hover:underline"
                            >
                                Open
                                <LuChevronRight aria-hidden className="size-4" />
                            </Link>
                        </td>
                    </tr>
                ))}
            </Table>
        );
    };

    return (
        <div className="mx-auto max-w-6xl">
            <PageHeader title="Class reports" subtitle="Reports made for a whole class or section. Open one to see each student’s report." />
            <Card noPadding>{renderBody()}</Card>
        </div>
    );
}
